/**
 * 녹화 관리 REST API 클라이언트 (BL-638).
 *
 * 서버 라우트: /dronecast/1.0/admin/recordings*
 * 인증: x-api-key + x-user-email 헤더 (authMiddleware)
 */

import axios from 'axios'
import { getAuthHeaders, getStoredAuth } from '@/services/auth.credentials'

const API_BASE_URL = '/dronecast/1.0/admin/recordings'

export interface RecordingListItem {
  recordingId: string
  castroomId: { gcs: string; drone: string; camera: string }
  startedAt: number
  endedAt: number
  durationMs: number
  format: 'webm' | 'mp4'
  trigger: string
  outputPath: string
  filename: string
  fileSize: number
  status: 'recording' | 'stopping' | 'completed' | 'failed' | 'orphan' | 'starting'
  error?: string
  mediaServerType: 'headless'
  createdAt: number
}

export interface RecordingListFilter {
  gcsId?: string
  droneName?: string
  cameraName?: string
  status?: string
  /** epoch ms */
  after?: number
  /** epoch ms */
  before?: number
}

export const recordingApiService = {
  async list(filter: RecordingListFilter = {}): Promise<RecordingListItem[]> {
    const params = new URLSearchParams()
    if (filter.gcsId) params.set('gcsId', filter.gcsId)
    if (filter.droneName) params.set('droneName', filter.droneName)
    if (filter.cameraName) params.set('cameraName', filter.cameraName)
    if (filter.status) params.set('status', filter.status)
    if (filter.after) params.set('after', String(filter.after))
    if (filter.before) params.set('before', String(filter.before))
    const url = params.toString() ? `${API_BASE_URL}?${params.toString()}` : API_BASE_URL
    const res = await axios.get(url, { headers: getAuthHeaders() })
    return (res.data?.recordings || []) as RecordingListItem[]
  },

  async getById(recordingId: string): Promise<RecordingListItem> {
    const res = await axios.get(`${API_BASE_URL}/${recordingId}`, { headers: getAuthHeaders() })
    return res.data as RecordingListItem
  },

  async deleteById(recordingId: string): Promise<void> {
    await axios.delete(`${API_BASE_URL}/${recordingId}`, { headers: getAuthHeaders() })
  },

  /**
   * 다운로드/미리보기 URL — `<a href>` 또는 `<video src>`에 직접 사용.
   * 헤더 주입이 불가능한 컨텍스트를 위해 쿼리 파라미터로 인증 정보 첨부.
   * 서버 authMiddleware가 헤더와 쿼리를 모두 허용한다.
   */
  downloadUrl(recordingId: string): string {
    const { apiKey, userEmail } = getStoredAuth()
    const params = new URLSearchParams()
    params.set('apiKey', apiKey)
    params.set('userEmail', userEmail)
    return `${API_BASE_URL}/${recordingId}/download?${params.toString()}`
  },
}
